import {useMemo} from 'react';
import {useAppSelector} from '@hooks';
import {MapView} from '@molecules';
import {
  type Area,
  type MeetingRoom,
  getAvailabilityStatus,
  getAvailabilityStatusFromData,
  getMarkerState,
  getMeetingRoomSuggestionByBuildingId,
} from '@lib/store';
import {useFacilityMarkers} from '../useFacilityMarkers';
import {RoomEventTarget} from '../../../../../../../submodules/map/mapiq-map/EventTarget';
import {AbsoluteViewportRestriction} from '../../../../../../../submodules/map/mapiq-map/AbsoluteViewportRestriction';
import {createSetStyleConfig, createSetStyleConfigOverrideEntry, createState, mergeState} from '@lib/utils';
import {RoomAvailabilityMarkerData, RoomAvailabilityStatus} from '@map/mapiq-map/markers/NodeMarkerData';
import {EventTarget} from '@map/mapiq-map/EventTarget';
import {useHereAndNow} from '@pages';

type HaNRoomsMapProps = {
  floorId: string;
  buildingId: string;
  onRoomClick: (e: RoomEventTarget) => void;
  onRoomHover?: (id: string | null) => void;
  onOutsideClick: () => void;
  hoverRoom: MeetingRoom | null;
  selectedRoom: MeetingRoom | Area | null;
  viewportRestrictions?: AbsoluteViewportRestriction;
};

export const HaNRoomsMap = ({
  buildingId,
  floorId,
  hoverRoom,
  onRoomHover,
  onRoomClick,
  viewportRestrictions,
  selectedRoom,
  onOutsideClick,
}: HaNRoomsMapProps) => {
  const {filterData, setHoverRoomId} = useHereAndNow();

  const rooms = filterData.rooms.filteredData;
  const roomSuggestionDataEntities = useAppSelector((state) => getMeetingRoomSuggestionByBuildingId(state, buildingId));

  const roomMarkers = useMemo<RoomAvailabilityMarkerData[]>(
    () =>
      rooms
        .filter((room) => room.floorId === floorId && room.suggestionData)
        .map(
          (room): RoomAvailabilityMarkerData => ({
            type: 'room_availability',
            nodeId: room.id,
            status: getAvailabilityStatusFromData(roomSuggestionDataEntities, room),
          }),
        )
        .filter(({status}) => status !== 'occupied'), // This variant hides occupied markers
    [rooms, floorId, roomSuggestionDataEntities],
  );

  const facilityMarkers = useFacilityMarkers(buildingId, floorId);

  const highlights = useMemo(() => [...roomMarkers, ...facilityMarkers], [roomMarkers, facilityMarkers]);

  const groupedRooms = useMemo(() => {
    return rooms.reduce<Record<RoomAvailabilityStatus, MeetingRoom[]>>(
      (acc, room) => {
        if (room.id !== selectedRoom?.id) {
          const roomSuggestion = room.suggestionData
            ? roomSuggestionDataEntities?.data[room.suggestionData]
            : undefined;

          const state = getAvailabilityStatus(roomSuggestion);

          acc[state].push(room);
        }
        return acc;
      },
      {available: [], occupied: [], unknown: [], untraceable: []},
    );
  }, [rooms, roomSuggestionDataEntities, selectedRoom]);

  const highlightedRooms = useMemo(
    () =>
      rooms.filter((room) => {
        // Treat all rooms as highlighted
        return room.id !== selectedRoom?.id && room.suggestionData;
      }),
    [rooms, selectedRoom],
  );

  const dimmedRooms = useMemo(
    () =>
      rooms.filter(
        (room) =>
          room.suggestionData && getMarkerState(roomSuggestionDataEntities?.data[room.suggestionData]) === 'dimmed',
      ),
    [rooms, roomSuggestionDataEntities],
  );

  const allRooms = useMemo(() => {
    const availableRoomState = createState(
      highlightedRooms.filter((room) => room.id !== selectedRoom?.id).map((room) => room.id),
      'highlighted',
    );

    const dimmedRoomStates = createState(
      dimmedRooms.filter((room) => room.id !== selectedRoom?.id).map((room) => room.id),
      'dimmed',
    );

    const selectedRoomState = createState(selectedRoom ? [selectedRoom.id] : [], 'selected');

    return [dimmedRoomStates, availableRoomState, selectedRoomState].reduce(mergeState);
  }, [highlightedRooms, dimmedRooms, selectedRoom]);

  const elementsToKeepInView = useMemo(() => (selectedRoom ? [selectedRoom.id] : undefined), [selectedRoom]);

  const nodeStyles = useMemo(() => {
    const available = 'available';
    const occupied = 'occupied';

    return createSetStyleConfig({
      preset: 'base',
      override: new Map([
        ...groupedRooms.available.map((room) => createSetStyleConfigOverrideEntry(room.id, available)),
        ...groupedRooms.occupied.map((room) => createSetStyleConfigOverrideEntry(room.id, occupied)),
        ...groupedRooms.unknown.map((room) => createSetStyleConfigOverrideEntry(room.id, 'seemsToBeAvailable')),
        ...groupedRooms.untraceable.map((room) => createSetStyleConfigOverrideEntry(room.id, 'base')),
        createSetStyleConfigOverrideEntry('room', 'available'),
      ]),
    });
  }, [groupedRooms]);

  const handleHover = (e: EventTarget) => {
    const validId = e.areaId ?? e.roomId ?? e.facilityId;
    if (validId) {
      setHoverRoomId?.(validId);
      return;
    }

    setHoverRoomId?.(null);
  };

  const handleClick = (e: RoomEventTarget) => {
    if (e.type === 'room') {
      onRoomClick(e);
      return;
    }

    onOutsideClick();
  };

  return (
    <MapView
      borderRadius={0}
      floorId={floorId}
      buildingId={buildingId}
      highlights={highlights}
      fullView={true}
      elementsToKeepInView={elementsToKeepInView}
      disablePointerEvents={false}
      buildingNodeStates={allRooms}
      buildingNodeTypeStates={new Map([['facility', 'dimmed']])}
      viewportRestrictions={viewportRestrictions}
      onClick={(e) => handleClick(e as RoomEventTarget)}
      spotlightId={hoverRoom?.floorId === floorId ? hoverRoom.id : null}
      type="preference"
      nodeStyles={nodeStyles}
      onHover={handleHover}
    />
  );
};
